// db/fsDB.js — IndexedDB wrapper

import { openDB } from "idb";

/* ─────────────────────────────────────────────────────────────
   DB CONFIG
───────────────────────────────────────────────────────────── */
const DB_NAME    = "SocialAppDB";
const DB_VERSION = 13; // bumped: added STATUS_STORE

const KEY_STORE = "crypto-keys";

const GROUP_KEY_PREFIX = "group_";
const STATUS_KEY_PREFIX = "status_";
const SIGNING_KEY_PREFIX = "signing_";
const SOLANA_KEY_PREFIX = "solana_";

const HANDLE_STORE = "fs-handles";

const MESSAGE_STORE      = "chat-messages";
const LIST_STORE         = "chat-list-cache";
export const POST_STORE = "feed-posts";
export const COMMENT_STORE = "post-comments";
export const STATUS_STORE = "statuses";
export const NOTIFICATION_STORE = "feed-notifications";
export const SCHEDULED_STORE = "scheduled_messages";


/* ─────────────────────────────────────────────────────────────
   DB INIT
───────────────────────────────────────────────────────────── */
export const dbPromise = openDB(DB_NAME, DB_VERSION, {
  upgrade(db) {

    /* 🔐 CRYPTO KEYS */
    if (!db.objectStoreNames.contains(KEY_STORE)) {
      db.createObjectStore(KEY_STORE);
    }

    /* 📁 FILE HANDLES */
    if (!db.objectStoreNames.contains(HANDLE_STORE)) {
      db.createObjectStore(HANDLE_STORE, { keyPath: "id" });
    }

    /* 💬 CHAT MESSAGES */
    if (!db.objectStoreNames.contains(MESSAGE_STORE)) {
      const store = db.createObjectStore(MESSAGE_STORE, { keyPath: "id" });
      store.createIndex("chatKey",   "chatKey");
      store.createIndex("timestamp", "timestamp");
    }

    /* 📋 CHAT LIST CACHE */
    if (!db.objectStoreNames.contains(LIST_STORE)) {
      const store = db.createObjectStore(LIST_STORE, { keyPath: "chatKey" });
      store.createIndex("lastActivityAt", "lastActivityAt");
    }

    /* 📝 POSTS
       MongoDB shape:
         postId          — UUID string  (keyPath)
         authorId        — chatId string   e.g. "489951863685@lid"
         authorUsername  — string
         text            — string | null
         media           — array
         replyToPostId   — string | null
         repostOfPostId  — string | null
         repostOfCommentId — string | null
         repostAuthorId  — string | null
         isRepost        — bool
         isQuote         — bool
         visibility      — "public" | …
         likesCount      — number
         repostsCount    — number
         commentsCount   — number
         viewsCount      — number
         engagementScore — number
         isEdited        — bool
         isDeleted       — bool
         createdAt       — ISO string / Date
         updatedAt       — ISO string / Date
    */
    if (db.objectStoreNames.contains(POST_STORE)) {
      // Drop old store on upgrade so indexes are rebuilt correctly
      db.deleteObjectStore(POST_STORE);
    }
    {
      const store = db.createObjectStore(POST_STORE, { keyPath: "postId" });
      store.createIndex("authorId",  "authorId");   // was "authorChatId" — fixed
      store.createIndex("createdAt", "createdAt");
      store.createIndex("updatedAt", "updatedAt");
      store.createIndex("isDeleted", "isDeleted");
    }

    /* 💬 COMMENTS
       MongoDB shape:
         commentId       — UUID string  (keyPath)
         postId          — UUID string
         parentCommentId — string | null
         author          — { chatId, username }   ← nested object
         text            — string
         quote           — object | null
         likesCount      — number
         repostsCount    — number
         commentsCount   — number
         viewsCount      — number
         isEdited        — bool
         isDeleted       — bool
         createdAt       — ISO string / Date
         updatedAt       — ISO string / Date
    */
    if (db.objectStoreNames.contains(COMMENT_STORE)) {
      db.deleteObjectStore(COMMENT_STORE);
    }
    {
      const store = db.createObjectStore(COMMENT_STORE, { keyPath: "commentId" });
      store.createIndex("postId",          "postId");
      store.createIndex("parentCommentId", "parentCommentId");
      store.createIndex("createdAt",       "createdAt");
      // author is a nested object — IDB can index nested paths
      store.createIndex("authorChatId",    "author.chatId");
    }

    /* 📸 STATUS */
    if (db.objectStoreNames.contains(STATUS_STORE)) {
      db.deleteObjectStore(STATUS_STORE);
    }

    {
      const store = db.createObjectStore(
        STATUS_STORE,
        {
          keyPath: "statusId",
        }
      );

      store.createIndex(
        "ownerChatId",
        "ownerChatId"
      );

      store.createIndex(
        "createdAt",
        "createdAt"
      );

      store.createIndex(
        "expiresAt",
        "expiresAt"
      );

      store.createIndex(
        "isDeleted",
        "isDeleted"
      );
    }

    /* 🔔 NOTIFICATIONS */
    if (!db.objectStoreNames.contains(NOTIFICATION_STORE)) {
      const store = db.createObjectStore(NOTIFICATION_STORE, { keyPath: "id" });
      store.createIndex("type",           "type");
      store.createIndex("postId",         "postId");
      store.createIndex("commentId",      "commentId");
      store.createIndex("lastActivityAt", "lastActivityAt");
      store.createIndex("unreadCount",    "unreadCount");
    }

    /* ⏰ SCHEDULED MESSAGES */
    if (!db.objectStoreNames.contains(SCHEDULED_STORE)) {
      db.createObjectStore(SCHEDULED_STORE, { keyPath: "msgId" });
    }
  },
});

/* ─────────────────────────────────────────────────────────────
   HANDLE STORE
───────────────────────────────────────────────────────────── */
export async function saveItem(id, value) {
  const db = await dbPromise;
  await db.put(HANDLE_STORE, { id, value });
}

export async function loadItem(id) {
  const db     = await dbPromise;
  const result = await db.get(HANDLE_STORE, id);
  return result?.value ?? null;
}

/* ─────────────────────────────────────────────────────────────
   MESSAGE STORE
───────────────────────────────────────────────────────────── */
export async function saveMessage(record) {
  const db       = await dbPromise;
  const existing = await db.get(MESSAGE_STORE, record.id);

  const normalizedRecord = {
    ...(existing || {}),
    ...record,
    timestamp: (() => {
      const t = record.timestamp ?? existing?.timestamp;
      if (!t) return Date.now();
      if (typeof t === "number") return t;
      const parsed = new Date(t).getTime();
      return isNaN(parsed) ? Date.now() : parsed;
    })(),
  };

  await db.put(MESSAGE_STORE, normalizedRecord);
  return true;
}

export async function loadMessages(chatKey, myChatId) {
  const db  = await dbPromise;
  const all = await db.getAllFromIndex(MESSAGE_STORE, "chatKey", chatKey);

  return all
    .filter((m) => !(m.deletedFor || []).includes(String(myChatId)))
    .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
}

export async function countMessages(chatKey) {
  const db = await dbPromise;
  return db.countFromIndex(MESSAGE_STORE, "chatKey", chatKey);
}

export async function markMessagesSeenInDB(chatKey, myChatId) {
  const db       = await dbPromise;
  const messages = await db.getAllFromIndex(MESSAGE_STORE, "chatKey", chatKey);
  if (!messages.length) return;

  const seenAt = new Date().toISOString();
  const tx     = db.transaction(MESSAGE_STORE, "readwrite");
  const store  = tx.objectStore(MESSAGE_STORE);

  for (const r of messages) {
    if (!r.fromMe && String(r.senderChatId) !== String(myChatId) && !r.seen) {
      await store.put({ ...r, seen: true, seenAt });
    }
  }

  await tx.done;
}

/* ─────────────────────────────────────────────────────────────
   DELETE ALL MESSAGES FOR A CHAT
───────────────────────────────────────────────────────────── */
export async function deleteChatMessages(chatKey) {
  if (!chatKey) return;

  const db = await dbPromise;

  const tx = db.transaction(
    MESSAGE_STORE,
    "readwrite"
  );

  const store = tx.objectStore(
    MESSAGE_STORE
  );

  const index = store.index(
    "chatKey"
  );

  const messages =
    await index.getAll(chatKey);

  for (const message of messages) {
    await store.delete(
      message.id
    );
  }

  await tx.done;
}

/* ─────────────────────────────────────────────────────────────
   POSTS
   Normalizes incoming data so both server documents (with
   authorId, ISO date strings) and optimistic client objects
   (with author.chatId, Date instances) are stored consistently.
───────────────────────────────────────────────────────────── */
export async function savePost(post) {
  if (!post?.postId) return;

  const db       = await dbPromise;
  const existing = await db.get(POST_STORE, post.postId);

  // Flatten author object if it came from an optimistic/socket payload
  // so the authorId index always has a plain string value.
  const authorId =
    post.authorId ??
    post.author?.chatId ??
    existing?.authorId ??
    null;

  const authorUsername =
    post.authorUsername ??
    post.author?.username ??
    existing?.authorUsername ??
    null;

  await db.put(POST_STORE, {
  ...(existing || {}),
  ...post,

  // =====================================================
  // NORMALIZED AUTHOR
  // =====================================================
  authorId,
  authorUsername,

  // =====================================================
  // MONGO-COMPATIBLE FALLBACK FIELDS
  // =====================================================
  _id:
    post._id ??
    existing?._id ??
    null,

  repostOfPostId:
    post.repostOfPostId ??
    existing?.repostOfPostId ??
    null,

  repostOfCommentId:
    post.repostOfCommentId ??
    existing?.repostOfCommentId ??
    null,

  repostAuthorId:
    post.repostAuthorId ??
    existing?.repostAuthorId ??
    null,

  isRepost:
    post.isRepost ??
    existing?.isRepost ??
    false,

  isQuote:
    post.isQuote ??
    existing?.isQuote ??
    false,

  reportsCount:
    post.reportsCount ??
    existing?.reportsCount ??
    0,

  isReported:
    post.isReported ??
    existing?.isReported ??
    false,

  engagementScore:
    post.engagementScore ??
    existing?.engagementScore ??
    0,

    supportsCount:
  post.supportsCount ??
  existing?.supportsCount ??
  0,

supportsAmount:
  post.supportsAmount ??
  existing?.supportsAmount ??
  0,

  isEdited:
    post.isEdited ??
    existing?.isEdited ??
    false,

  isDeleted:
    post.isDeleted ??
    existing?.isDeleted ??
    false,

  __v:
    post.__v ??
    existing?.__v ??
    0,

  // =====================================================
  // NORMALIZED DATES
  // =====================================================
  createdAt: _toISO(
    post.createdAt ??
    existing?.createdAt
  ),

  updatedAt:
    _toISO(post.updatedAt) ??
    existing?.updatedAt ??
    _toISO(new Date()),
});
}

export async function loadPosts() {
  const db    = await dbPromise;
  const posts = await db.getAll(POST_STORE);

  return posts
    .filter((p) => !p.isDeleted)
    .sort((a, b) => _tsOf(b.createdAt) - _tsOf(a.createdAt));
}

export async function loadPostById(postId) {
  const db = await dbPromise;
  return db.get(POST_STORE, postId) ?? null;
}

export async function deletePost(postId) {
  const db = await dbPromise;
  await db.delete(POST_STORE, postId);
}

/* ─────────────────────────────────────────────────────────────
   COMMENTS
   Normalises the nested author object so the authorChatId
   index always has a plain string value.
───────────────────────────────────────────────────────────── */
export async function saveComment(comment) {
  if (!comment?.commentId) return;

  const db       = await dbPromise;
  const existing = await db.get(COMMENT_STORE, comment.commentId);

  // Ensure author is always a plain object { chatId, username }
  const author = {
    chatId:   comment.author?.chatId   ?? existing?.author?.chatId   ?? null,
    username: comment.author?.username ?? existing?.author?.username ?? null,
  };

  await db.put(COMMENT_STORE, {
  ...(existing || {}),
  ...comment,

  // =====================================================
  // NORMALIZED AUTHOR
  // =====================================================
  author,

  // =====================================================
  // MONGO-COMPATIBLE FALLBACK FIELDS
  // =====================================================
  _id:
    comment._id ??
    existing?._id ??
    null,

  quote:
    comment.quote ??
    existing?.quote ??
    null,

  repostOfCommentId:
    comment.repostOfCommentId ??
    existing?.repostOfCommentId ??
    null,

  repostAuthorId:
    comment.repostAuthorId ??
    existing?.repostAuthorId ??
    null,

  isRepost:
    comment.isRepost ??
    existing?.isRepost ??
    false,

  isQuote:
    comment.isQuote ??
    existing?.isQuote ??
    false,

  likesCount:
    comment.likesCount ??
    existing?.likesCount ??
    0,

  repostsCount:
    comment.repostsCount ??
    existing?.repostsCount ??
    0,

  commentsCount:
    comment.commentsCount ??
    existing?.commentsCount ??
    0,

  viewsCount:
    comment.viewsCount ??
    existing?.viewsCount ??
    0,

    supportsCount:
  comment.supportsCount ??
  existing?.supportsCount ??
  0,

supportsAmount:
  comment.supportsAmount ??
  existing?.supportsAmount ??
  0,

  reportsCount:
    comment.reportsCount ??
    existing?.reportsCount ??
    0,

  isReported:
    comment.isReported ??
    existing?.isReported ??
    false,

  isEdited:
    comment.isEdited ??
    existing?.isEdited ??
    false,

  isDeleted:
    comment.isDeleted ??
    existing?.isDeleted ??
    false,

  __v:
    comment.__v ??
    existing?.__v ??
    0,

  // =====================================================
  // NORMALIZED DATES
  // =====================================================
  createdAt: _toISO(
    comment.createdAt ??
    existing?.createdAt
  ),

  updatedAt:
    _toISO(comment.updatedAt) ??
    existing?.updatedAt ??
    _toISO(new Date()),
});
}

export async function loadComments(postId) {
  const db       = await dbPromise;
  const comments = await db.getAllFromIndex(COMMENT_STORE, "postId", postId);

  return comments
    .filter((c) => !c.isDeleted)
    .sort((a, b) => _tsOf(a.createdAt) - _tsOf(b.createdAt));
}

export async function loadCommentById(commentId) {
  const db = await dbPromise;
  return db.get(COMMENT_STORE, commentId) ?? null;
}

export async function deleteComment(commentId) {
  const db = await dbPromise;
  await db.delete(COMMENT_STORE, commentId);
}

/* ─────────────────────────────────────────────────────────────
   STATUS
   Normalises dates so both server documents (ISO strings) and
   optimistic client objects (Date instances) are stored
   consistently. Statuses are treated as expired/deleted the
   same way for display purposes.
───────────────────────────────────────────────────────────── */
export async function saveStatus(status) {

  if (!status?.statusId) {
    return;
  }

  const db =
    await dbPromise;

  const existing =
    await db.get(
      STATUS_STORE,
      status.statusId
    );

  await db.put(
    STATUS_STORE,
    {
      ...(existing || {}),
      ...status,

      ownerChatId:
        status.ownerChatId ??
        existing?.ownerChatId ??
        null,

      statusType:
        status.statusType ??
        existing?.statusType ??
        "text",

      encryptedText:
        status.encryptedText ??
        existing?.encryptedText ??
        [],

      encryptedMedia:
        status.encryptedMedia ??
        existing?.encryptedMedia ??
        [],

      caption:
        status.caption ??
        existing?.caption ??
        [],

      thumbnailEncrypted:
        status.thumbnailEncrypted ??
        existing?.thumbnailEncrypted ??
        [],

      mediaMimeType:
        status.mediaMimeType ??
        existing?.mediaMimeType ??
        null,

      mediaSize:
        status.mediaSize ??
        existing?.mediaSize ??
        null,

      mediaDuration:
        status.mediaDuration ??
        existing?.mediaDuration ??
        null,

      viewsCount:
        status.viewsCount ??
        existing?.viewsCount ??
        0,

      isDeleted:
        status.isDeleted ??
        existing?.isDeleted ??
        false,

      createdAt:
        _toISO(
          status.createdAt ??
          existing?.createdAt
        ),

      expiresAt:
        _toISO(
          status.expiresAt ??
          existing?.expiresAt
        ),
    }
  );
}

export async function loadStatuses() {
  const db = await dbPromise;

  const statuses = await db.getAll(
    STATUS_STORE
  );

  return statuses
    .filter(
      (s) =>
        !s.isDeleted &&
        new Date(s.expiresAt) > new Date()
    )
    .sort(
      (a, b) => _tsOf(b.createdAt) - _tsOf(a.createdAt)
    );
}

export async function loadStatusesByOwner(ownerChatId) {
  const db = await dbPromise;

  const statuses = await db.getAllFromIndex(
    STATUS_STORE,
    "ownerChatId",
    ownerChatId
  );

  return statuses
    .filter(
      (s) =>
        !s.isDeleted &&
        new Date(s.expiresAt) > new Date()
    )
    .sort(
      (a, b) => _tsOf(a.createdAt) - _tsOf(b.createdAt)
    );
}

export async function deleteStatus(statusId) {

  if (!statusId) {
    return;
  }

  const db = await dbPromise;

  await db.delete(
    STATUS_STORE,
    statusId
  );

}

/* ─────────────────────────────────────────────────────────────
   NOTIFICATIONS
───────────────────────────────────────────────────────────── */
export async function saveNotification(notification) {

  if (!notification) return;

  /* =====================================================
     SUPPORTS = ONE DOCUMENT PER NOTIFICATION

     LIKES / REPOSTS / COMMENTS = AGGREGATED
  ===================================================== */
  const id =
    notification.type === "post_support" ||
    notification.type === "comment_support"
      ? (
          notification.notificationId ||
          notification.id
        )
      : (
          notification.id ||
          [
            notification.type || "unknown",
            notification.postId || "nopost",
            notification.commentId || "nocomment",
          ].join("_")
        );

  const db = await dbPromise;

  const existing =
    await db.get(
      NOTIFICATION_STORE,
      id
    );

  await db.put(
    NOTIFICATION_STORE,
    {
      ...(existing || {}),
      ...notification,

      id,

      lastActivityAt:
        notification.lastActivityAt ??
        existing?.lastActivityAt ??
        Date.now(),
    }
  );
}

export async function loadNotifications() {
  const db            = await dbPromise;
  const notifications = await db.getAll(NOTIFICATION_STORE);

  return notifications.sort(
    (a, b) => _tsOf(b.lastActivityAt) - _tsOf(a.lastActivityAt)
  );
}

export async function clearNotifications() {
  const db = await dbPromise;
  await db.clear(NOTIFICATION_STORE);
}

/* ─────────────────────────────────────────────────────────────
   CHAT LIST CACHE
───────────────────────────────────────────────────────────── */
export async function updateUnreadCount(chatKey, unreadCount) {
  const db       = await dbPromise;
  const existing = await db.get(LIST_STORE, chatKey);
  if (!existing) return;
  await db.put(LIST_STORE, { ...existing, unreadCount });
}

export async function updateLastMessage(chatKey, payload) {
  const db       = await dbPromise;
  const existing = await db.get(LIST_STORE, chatKey);
  if (!existing) return;

  await db.put(LIST_STORE, {
    ...existing,
    lastMessage:    payload.lastMessage    ?? existing.lastMessage,
    lastActivityAt: payload.lastActivityAt ?? existing.lastActivityAt,
  });
}

/* ─────────────────────────────────────────────────────────────
   DELETE MESSAGE HELPERS
───────────────────────────────────────────────────────────── */
export const deleteMessageFromDB = async (msgId) => {
  const db    = await dbPromise;
  const tx    = db.transaction(MESSAGE_STORE, "readwrite");
  const store = tx.objectStore(MESSAGE_STORE);

  const existing = await store.get(msgId);
  if (!existing) { await tx.done; return; }

  await store.delete(msgId);
  await tx.done;
};

export const markMessageDeletedForEveryone = async (msgId) => {
  const db    = await dbPromise;
  const tx    = db.transaction(MESSAGE_STORE, "readwrite");
  const store = tx.objectStore(MESSAGE_STORE);

  const existing = await store.get(msgId);
  if (!existing) { await tx.done; return; }

  await store.put({ ...existing, isDeleted: true });
  await tx.done;
};

export const markMessageDeletedForMe = async (msgId, myChatId) => {
  const db    = await dbPromise;
  const tx    = db.transaction(MESSAGE_STORE, "readwrite");
  const store = tx.objectStore(MESSAGE_STORE);

  const existing = await store.get(msgId);
  if (!existing) { await tx.done; return; }

  const deletedFor = new Set(existing.deletedFor || []);
  deletedFor.add(String(myChatId));

  await store.put({ ...existing, deletedFor: Array.from(deletedFor) });
  await tx.done;
};

/* ─────────────────────────────────────────────────────────────
   CRYPTO KEYS
───────────────────────────────────────────────────────────── */
export async function saveGroupCryptoKey(groupId, cryptoKey) {
  const db = await dbPromise;
  await db.put(KEY_STORE, cryptoKey, `${GROUP_KEY_PREFIX}${groupId}`);
}

export async function loadGroupCryptoKey(groupId) {
  const db = await dbPromise;
  return db.get(KEY_STORE, `${GROUP_KEY_PREFIX}${groupId}`);
}

/* ─────────────────────────────────────────────────────────────
   STATUS CRYPTO KEYS
───────────────────────────────────────────────────────────── */
export async function saveStatusCryptoKey(
  ownerChatId,
  cryptoKey
) {

  if (!ownerChatId || !cryptoKey) {
    return;
  }

  const db = await dbPromise;

  await db.put(
    KEY_STORE,
    cryptoKey,
    `${STATUS_KEY_PREFIX}${ownerChatId}`
  );

}

export async function loadStatusCryptoKey(
  ownerChatId
) {

  if (!ownerChatId) {
    return null;
  }

  const db = await dbPromise;

  return db.get(
    KEY_STORE,
    `${STATUS_KEY_PREFIX}${ownerChatId}`
  );

}


export async function savePrivateKey(chatId, cryptoKey) {
  const db = await dbPromise;
  await db.put(KEY_STORE, cryptoKey, `priv_${chatId}`);
}

export async function loadPrivateKeyFromDB(chatId) {
  const db = await dbPromise;
  return db.get(KEY_STORE, `priv_${chatId}`);
}

/* ─────────────────────────────────────────────────────────────
   USWD SIGNING KEYS
───────────────────────────────────────────────────────────── */

export async function saveSigningPrivateKey(userId, cryptoKey) {
  const db = await dbPromise;

  await db.put(
    KEY_STORE,
    cryptoKey,
    `${SIGNING_KEY_PREFIX}${userId}`
  );
}

export async function loadSigningPrivateKey(userId) {
  const db = await dbPromise;

  return db.get(
    KEY_STORE,
    `${SIGNING_KEY_PREFIX}${userId}`
  );
}

export async function deleteSigningPrivateKey(userId) {
  const db = await dbPromise;

  await db.delete(
    KEY_STORE,
    `${SIGNING_KEY_PREFIX}${userId}`
  );
}

export async function saveSolanaPrivateKey(
  userId,
  secretKey
) {

  const db =
    await dbPromise;

  await db.put(

    KEY_STORE,

    secretKey,

    `${SOLANA_KEY_PREFIX}${userId}`

  );

}

export async function loadSolanaPrivateKey(
  userId
) {

  const db =
    await dbPromise;

  return db.get(

    KEY_STORE,

    `${SOLANA_KEY_PREFIX}${userId}`

  );

}

/* ─────────────────────────────────────────────────────────────
   SCHEDULED MESSAGES
───────────────────────────────────────────────────────────── */
export async function saveScheduledMessage(record) {
  const db       = await dbPromise;
  const existing = await db.get(SCHEDULED_STORE, record.msgId);
  await db.put(SCHEDULED_STORE, { ...(existing || {}), ...record });
}

export async function deleteScheduledMessage(msgId) {
  const db = await dbPromise;
  await db.delete(SCHEDULED_STORE, msgId);
}

export async function getAllScheduledMessages() {
  const db = await dbPromise;
  return db.getAll(SCHEDULED_STORE);
}

export async function getScheduledMessagesByBatchId(batchId) {
  if (!batchId) return [];
  const db  = await dbPromise;
  const all = await db.getAll(SCHEDULED_STORE);
  return all.filter((r) => r.batchId === batchId);
}

/* ─────────────────────────────────────────────────────────────
   CLEAR APP DATA (NORMAL)
   Does NOT remove crypto keys or file handles.
───────────────────────────────────────────────────────────── */
export async function clearAllIDB() {

  const db = await dbPromise;

  const tx = db.transaction(
    [
      MESSAGE_STORE,
      LIST_STORE,
      POST_STORE,
      COMMENT_STORE,
      STATUS_STORE,
      NOTIFICATION_STORE,
      SCHEDULED_STORE,
    ],
    "readwrite"
  );

  await Promise.all([
    tx.objectStore(MESSAGE_STORE).clear(),
    tx.objectStore(LIST_STORE).clear(),
    tx.objectStore(POST_STORE).clear(),
    tx.objectStore(COMMENT_STORE).clear(),
    tx.objectStore(
      STATUS_STORE
    ).clear(),
    tx.objectStore(NOTIFICATION_STORE).clear(),
    tx.objectStore(SCHEDULED_STORE).clear(),
  ]);

  await tx.done;

}

/* ─────────────────────────────────────────────────────────────
   CLEAR ACCOUNT DATA
   Used ONLY when the user permanently deletes the account.
   Removes EVERYTHING including cryptographic material.
───────────────────────────────────────────────────────────── */
export async function clearAccountIDB() {

  // First clear all normal application data
  await clearAllIDB();

  const db = await dbPromise;

  const tx = db.transaction(
    [
      KEY_STORE,
      HANDLE_STORE,
    ],
    "readwrite"
  );

  await Promise.all([
    tx.objectStore(KEY_STORE).clear(),
    tx.objectStore(HANDLE_STORE).clear(),
  ]);

  await tx.done;

}
/* ─────────────────────────────────────────────────────────────
   INTERNAL HELPERS
───────────────────────────────────────────────────────────── */

/** Convert any date value to an ISO string, or null if invalid. */
function _toISO(value) {
  if (!value) return null;
  if (typeof value === "string") return value; // already ISO
  if (value instanceof Date)     return value.toISOString();
  if (typeof value === "number") return new Date(value).toISOString();
  // MongoDB BSON Date objects have a toISOString method too
  if (typeof value?.toISOString === "function") return value.toISOString();
  return null;
}

/** Return a numeric timestamp from any date value, for sorting. */
function _tsOf(value) {
  if (!value) return 0;
  if (typeof value === "number") return value;
  const ts = new Date(value).getTime();
  return isNaN(ts) ? 0 : ts;
}